import React from "react";
import { Select, ActionIcon, Tooltip } from "@mantine/core";
import { IconLanguage } from "@tabler/icons-react";
import { useLanguage } from "./LanguageContext";
import { useTranslation } from "react-i18next";
import { Colors } from "../data";
import { useMantineTheme } from "@mantine/core";

interface LanguageSelectorProps {
  scrollY?: number;
  theme?: string;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  scrollY = 0,
  theme = "light",
}) => {
  const { currentLanguage, changeLanguage, availableLanguages } = useLanguage();
  const { t } = useTranslation();
  const themeObj = useMantineTheme();

  const getTextColor = () => {
    if (scrollY > 40) return Colors.White;
    return theme === "dark" ? "var(--text-primary)" : Colors.PurplePrimary;
  };

  const getHoverColor = () => {
    if (scrollY > 40) return "rgba(255, 255, 255, 0.15)";
    return theme === "dark"
      ? "rgba(255, 255, 255, 0.1)"
      : "rgba(98, 0, 238, 0.1)";
  };

  return (
    <Tooltip label={t("language.selectLanguage")} position="bottom" withArrow>
      <Select
        value={currentLanguage}
        onChange={(value) => value && changeLanguage(value)}
        data={availableLanguages
          .map((lang) => ({
            value: lang.code,
            label: lang.nativeName,
          }))
          .concat([
            { value: "de", label: "Deutsch" },
            { value: "fr", label: "Français" },
          ])}
        size="xs"
        styles={{
          root: {
            minWidth: "50px",
            background:
              theme === "dark"
                ? themeObj.colors.dark[7]
                : scrollY > 40
                ? "#ede9fe"
                : "#f6f6fa",
            borderRadius: "6px",
            boxShadow:
              theme === "dark"
                ? "0 2px 8px 0 rgba(80,0,120,0.13)"
                : "0 2px 8px 0 rgba(80,0,120,0.08)",
            border:
              theme === "dark"
                ? "1px solid #3b2f5a"
                : scrollY > 40
                ? "1.5px solid #c7bfff"
                : "1px solid #d1d5db",
            padding: "0 2px",
            display: "flex",
            alignItems: "center",
            transition: "all 0.2s cubic-bezier(.4,0,.2,1)",
          },
          input: {
            backgroundColor:
              theme === "dark"
                ? themeObj.colors.dark[7]
                : scrollY > 40
                ? "#ede9fe"
                : "#f6f6fa",
            border: "none",
            color: theme === "dark" ? "#fff" : "#2d225a",
            fontWeight: 500,
            fontSize: "0.8rem",
            padding: "4px 6px 4px 22px",
            borderRadius: "6px",
            boxShadow: "none",
            cursor: "pointer",
            transition: "all 0.3s cubic-bezier(.4,0,.2,1)",
            minHeight: "26px",
            "&:hover": {
              backgroundColor:
                theme === "dark" ? themeObj.colors.dark[6] : "#ece7fa",
              transform: "scale(1.03)",
            },
          },
          dropdown: {
            backgroundColor:
              theme === "dark" ? themeObj.colors.dark[7] : "#fff",
            border:
              theme === "dark" ? "1px solid #3b2f5a" : "1px solid #d1d5db",
            borderRadius: "6px",
            boxShadow:
              theme === "dark"
                ? "0 4px 16px 0 rgba(80,0,120,0.18)"
                : "0 4px 16px 0 rgba(80,0,120,0.10)",
            marginTop: 6,
          },
          option: {
            color: theme === "dark" ? "#e0e0ff" : "#2d225a",
            backgroundColor: "transparent",
            fontWeight: 500,
            fontSize: "0.85rem",
            borderRadius: "4px",
            margin: "1px 0",
            padding: "5px 10px",
            transition: "all 0.2s cubic-bezier(.4,0,.2,1)",
            "&:hover": {
              backgroundColor: theme === "dark" ? "#2d225a" : "#ede9fe",
              color: theme === "dark" ? "#fff" : "#7c3aed",
            },
            "&[data-selected]": {
              backgroundColor: theme === "dark" ? "#7c3aed" : "#7c3aed",
              color: "#fff",
            },
          },
        }}
        rightSection={
          <IconLanguage
            size={12}
            style={{
              color: theme === "dark" ? "#b794f4" : "#7c3aed",
              marginRight: 3,
              marginLeft: -16,
              position: "absolute",
              left: 4,
              top: "50%",
              transform: "translateY(-50%)",
            }}
          />
        }
      />
    </Tooltip>
  );
};

export default LanguageSelector;
